import type { UserInfo, SceneInfo, MemoirsInfo } from '~/types/interfaces'
import { useApiBase } from './useApiBase'

export interface GameNotification {
  notificationId: string
  title: string
  body: string
  icon: string
  createdAt: string
}

export interface AdvisorCost {
  advisorId: string
  name: string
  mpCost: number
}

export interface GetGameStateResponse {
  user: UserInfo
  scenes: Record<string, SceneInfo>
  rules: Partial<Record<string, unknown>>
  notifications: GameNotification[]
  advisorCosts?: AdvisorCost[]
  /** 羽根筆が書いたとき（implement）に取る MP（#938） */
  quillMpCost?: number
  /** 冒険の手記（#900）。 */
  memoirs?: MemoirsInfo
}

/**
 * スキルのカタログの 1 枚（#933）。**まだ手に入れていないスキルは kind と acquired だけ**
 * （名前も中身もサーバが送らない）。手に入れたスキルでも、まだの型は数（notYetCount）だけ。
 */
export interface SkillCard {
  kind: 'pyxel' | 'grammar' | 'technique' | 'genre'
  acquired: boolean
  skillId?: string | null
  name?: string | null
  /** 何をするか（人向け。AI の本質とは別。#960） */
  description?: string | null
  /** ゲームにもたらす面白さ（#957） */
  appeal?: string | null
  /** 並び（手に入るいちばん早いワンダーの順） */
  order?: number | null
  applications: string[]
  /** 例: お手本の関数（手に入れた模範ごと。#957） */
  examples?: { where?: string | null, code: string, highlight?: number[] }[]
  /** 改造ポイント（人向け）・コツ（#957） */
  remixPoints?: string[]
  /** コツ */
  tips?: string[]
  history: { text: string, sources: string[] }[]
  known: string[]
  notYetCount: number
  learnedAt: string[]
  /** まだのスキルの説明の冒頭（10 文字＋「……」） */
  teaser?: string | null
  /** 候補のワンダーのうち、アンロック済みでまだクリアしていないもの（いま行ける場所） */
  unlockedAt?: string[]
  /** learnedAt と同じ並びの、ワンダーのエリアの ID（チップをエリアの色で塗る） */
  learnedAtAreas?: (string | null)[]
  /** まだの文法のスキルの解放条件（経験値） */
  exp?: number | null
  /** 型ごと（Pyxel の基本・Python の書き方は型を 1 枚ずつ並べる）。まだの型は acquired と（名前を出す種類だけ）label。
   *  visible は段のある型（Python の書き方）だけ: まだだが見えているか */
  types: { acquired: boolean, visible?: boolean | null, copyable?: boolean, label?: string | null, key?: string | null, description?: string | null, teaser?: string | null, history: { text: string, sources: string[] }[], learnedAt: string[], learnedAtAreas?: (string | null)[], unlockedAt?: string[], exp?: number | null, applications?: string[], appeal?: string | null, order?: number | null, remixPoints?: string[], tips?: string[], example?: { where?: string | null, code: string, highlight?: number[] } | null }[]
}

export interface ListSkillsResponse {
  /** Pyxel の基本・Python の書き方・テクニック・ジャンル（kind で分かれる。#960） */
  skills: SkillCard[]
}

export interface GetGameNotificationsResponse {
  notifications: GameNotification[]
}

export function useGameStateApi() {
  const { apiBase, authenticatedFetch } = useApiBase()

  const getGameState = async () => {
    return authenticatedFetch<GetGameStateResponse>(
      `${apiBase}/api/v1/state`,
      { method: 'GET' },
    )
  }

  const getGameNotifications = async () => {
    return authenticatedFetch<GetGameNotificationsResponse>(
      `${apiBase}/api/v1/state/notifications`,
      { method: 'GET' },
    )
  }

  const listSkills = async () => {
    return authenticatedFetch<ListSkillsResponse>(
      `${apiBase}/api/v1/skills`,
      { method: 'GET' },
    )
  }

  return { getGameState, getGameNotifications, listSkills }
}
